import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import {
  fetchIncomes, addIncome, updateIncome, deleteIncome,
  fetchExpenses, addExpense, updateExpense, deleteExpense,
  fetchExceptions, createException, deleteException,
} from '../slices/recurringSlice';
import { formatAmount } from '../utils/formatHelpers';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';

// Página de gestión de series recurrentes (ingresos y gastos). Permite
// crear/editar/pausar/eliminar una serie completa, y gestionar
// excepciones puntuales (omitir o editar una sola ocurrencia) sin
// afectar el resto de la serie — según lo pedido: recurrencia "completa"
// con fecha de fin, pausar/saltar una ocurrencia, y editar solo una
// instancia.

// 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
// ingles?"): categorias se queda como los mismos 9 valores fijos EN
// ESPAÑOL de siempre -- son el mismo texto libre que guarda
// Transaction.categoria/Budget.categoria en toda la app (ver
// traducirCategoria en TransactionsPage.js: "value se mantiene crudo...
// solo el texto visible se traduce"). Si esta página guardara "Salary" en
// vez de "Salario" para un usuario en inglés, esa serie recurrente dejaría
// de calzar con los presupuestos y filtros de categoría del resto de la
// app, que siguen comparando contra el string en español. Por eso acá se
// traduce solo el LABEL mostrado (ver categoriaLabel), nunca el value.
const categorias = [
  'Salario', 'Alquiler', 'Alimentación', 'Transporte', 'Servicios',
  'Entretenimiento', 'Salud', 'Educación', 'Otro',
];
const CATEGORIA_LABELS_EN = {
  Salario: 'Salary', Alquiler: 'Rent', Alimentación: 'Food & Dining', Transporte: 'Transport',
  Servicios: 'Utilities', Entretenimiento: 'Entertainment', Salud: 'Health', Educación: 'Education', Otro: 'Other',
};
const categoriaLabel = (categoria, isSpanish) => (isSpanish ? categoria : (CATEGORIA_LABELS_EN[categoria] || categoria));

// 31 jul 2026: se agregó "Diario" y "Quincenal" pasó a significar los
// días 15 y 30 de cada mes (fijo), no cada 15 días flotando — ver
// _generate_occurrences en calendar.py.
const makeFrecuencias = (isSpanish) => [
  { value: 'diario', label: isSpanish ? 'Diario' : 'Daily' },
  { value: 'semanal', label: isSpanish ? 'Semanal' : 'Weekly' },
  { value: 'quincenal', label: isSpanish ? 'Días 15 y 30' : 'Days 15 and 30' },
  { value: 'mensual', label: isSpanish ? 'Mensual' : 'Monthly' },
];

const hoyISO = () => new Date().toISOString().slice(0, 10);

// 30 ago 2026 (Augusto: "se necesita colocar tanto en Soles como su
// equivalente en Dólares y viceversa" -- ver AskUserQuestion, eligió "dos
// columnas sin convertir" en vez de una conversión real con tipo de
// cambio, igual que ya se hace en Transacciones): cada serie recurrente
// ahora guarda su propia moneda (antes no existía el campo, así que
// suscripciones en dólares como Netflix/Anthropic se mostraban con el
// mismo formato que un pago en soles). "PEN" en el formulario se traduce
// a null al guardar (ver buildCreatePayload) -- mismo criterio que
// Transaction.moneda en toda la app.
const emptyItem = {
  nombre: '',
  monto: '',
  frecuencia: 'mensual',
  categoria: 'Otro',
  moneda: 'PEN',
  // 21 sep 2026 (Augusto: "cuentas recurrentes no incluyen la cuenta o
  // tarjeta donde se cargan"). El backend ya guardaba account_id en
  // Income/Expense y el calendario YA filtra las series por cuenta (ver
  // calendar.py) -- lo que faltaba era el campo en pantalla. Sin él,
  // cada serie nacía sin cuenta y desaparecía del calendario apenas
  // filtrabas por una, sin explicación visible.
  account_id: '',
  fecha_inicio: hoyISO(),
  fecha_fin: '',
};

// Igual que esMonedaSoles/esMonedaDolares en TransactionsPage.js.
const esSoles = (item) => !item.moneda || item.moneda.toUpperCase() === 'PEN';
const esDolares = (item) => (item.moneda || '').toUpperCase() === 'USD';

const RecurringPage = () => {
  const dispatch = useDispatch();
  const incomes = useSelector((state) => state.recurring.incomes);
  const expenses = useSelector((state) => state.recurring.expenses);
  const loading = useSelector((state) => state.recurring.loading);
  const error = useSelector((state) => state.recurring.error);
  const exceptionsByItem = useSelector((state) => state.recurring.exceptionsByItem);
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const frecuencias = useMemo(() => makeFrecuencias(isSpanish), [isSpanish]);
  const frecuenciaLabel = (value) => frecuencias.find((f) => f.value === value)?.label || value;

  useEffect(() => {
    dispatch(fetchIncomes());
    dispatch(fetchExpenses());
  }, [dispatch]);

  // --- Diálogo de crear/editar (compartido entre ingresos y gastos) ---
  // Las cuentas para el selector de "dónde se carga". Vienen ya
  // ordenadas alfabéticamente desde el backend (ver dashboard.py).
  const [cuentas, setCuentas] = useState([]);
  useEffect(() => {
    apiClient.get('/dashboard/accounts')
      .then((res) => setCuentas(res.data || []))
      .catch(() => setCuentas([]));
  }, []);
  const nombreCuenta = (id) => cuentas.find((c) => c.id === id)?.nombre || null;

  const [openNew, setOpenNew] = useState(false);
  const [nuevoTipo, setNuevoTipo] = useState('ingreso'); // 'ingreso' | 'gasto'
  const [nuevoItem, setNuevoItem] = useState(emptyItem);

  const [openEdit, setOpenEdit] = useState(false);
  const [itemEditando, setItemEditando] = useState(null); // incluye _tipo interno

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { tipo, id }

  const handleOpenNew = (tipo) => {
    setNuevoTipo(tipo);
    setNuevoItem({ ...emptyItem, categoria: tipo === 'ingreso' ? 'Salario' : 'Otro' });
    setOpenNew(true);
  };
  const handleCloseNew = () => {
    setOpenNew(false);
    setNuevoItem(emptyItem);
  };

  const handleOpenEdit = (item, tipo) => {
    setItemEditando({
      ...item,
      _tipo: tipo,
      moneda: esDolares(item) ? 'USD' : 'PEN',
      account_id: item.account_id || '',
      fecha_fin: item.fecha_fin || '',
    });
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setItemEditando(null);
  };

  const handleDeleteOpen = (tipo, id) => {
    setDeleteTarget({ tipo, id });
    setOpenDelete(true);
  };
  const handleDeleteClose = () => {
    setDeleteTarget(null);
    setOpenDelete(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevoItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setItemEditando((prev) => ({ ...prev, [name]: value }));
  };

  const buildCreatePayload = (data) => ({
    nombre: data.nombre?.trim(),
    monto: parseFloat(data.monto) || 0,
    frecuencia: data.frecuencia,
    categoria: data.categoria || null,
    moneda: data.moneda === 'USD' ? 'USD' : null,
    account_id: data.account_id || null,
    fecha_inicio: data.fecha_inicio,
    fecha_fin: data.fecha_fin || null,
  });

  const handleCreate = async () => {
    if (!nuevoItem.nombre?.trim() || !nuevoItem.monto || !nuevoItem.fecha_inicio) return;
    const payload = buildCreatePayload(nuevoItem);
    if (nuevoTipo === 'ingreso') {
      await dispatch(addIncome(payload));
    } else {
      await dispatch(addExpense(payload));
    }
    handleCloseNew();
  };

  const handleUpdateSubmit = async () => {
    if (!itemEditando?.nombre?.trim()) return;
    const payload = {
      nombre: itemEditando.nombre.trim(),
      monto: parseFloat(itemEditando.monto) || 0,
      frecuencia: itemEditando.frecuencia,
      categoria: itemEditando.categoria || null,
      moneda: itemEditando.moneda === 'USD' ? 'USD' : null,
      account_id: itemEditando.account_id || null,
      fecha_inicio: itemEditando.fecha_inicio,
      fecha_fin: itemEditando.fecha_fin || null,
      activo: itemEditando.activo,
    };
    if (itemEditando._tipo === 'ingreso') {
      await dispatch(updateIncome({ id: itemEditando.id, data: payload }));
    } else {
      await dispatch(updateExpense({ id: itemEditando.id, data: payload }));
    }
    handleCloseEdit();
  };

  const handleTogglePausa = async (item, tipo) => {
    const payload = { activo: !item.activo };
    if (tipo === 'ingreso') {
      await dispatch(updateIncome({ id: item.id, data: payload }));
    } else {
      await dispatch(updateExpense({ id: item.id, data: payload }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.tipo === 'ingreso') {
      await dispatch(deleteIncome(deleteTarget.id));
    } else {
      await dispatch(deleteExpense(deleteTarget.id));
    }
    handleDeleteClose();
  };

  // --- Diálogo de excepciones (omitir / editar una ocurrencia) ---
  const [openExc, setOpenExc] = useState(false);
  const [excTarget, setExcTarget] = useState(null); // { tipo, id, nombre }
  const [nuevaExc, setNuevaExc] = useState({ fecha_ocurrencia: hoyISO(), accion: 'omitir', monto_override: '', descripcion_override: '' });

  const handleOpenExc = (tipo, id, nombre) => {
    setExcTarget({ tipo, id, nombre });
    setNuevaExc({ fecha_ocurrencia: hoyISO(), accion: 'omitir', monto_override: '', descripcion_override: '' });
    dispatch(fetchExceptions({ itemTipo: tipo, itemId: id }));
    setOpenExc(true);
  };
  const handleCloseExc = () => {
    setOpenExc(false);
    setExcTarget(null);
  };

  const handleExcChange = (e) => {
    const { name, value } = e.target;
    setNuevaExc((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateExc = async () => {
    if (!excTarget || !nuevaExc.fecha_ocurrencia) return;
    const data = {
      fecha_ocurrencia: nuevaExc.fecha_ocurrencia,
      accion: nuevaExc.accion,
      monto_override: nuevaExc.accion === 'editar' && nuevaExc.monto_override !== '' ? parseFloat(nuevaExc.monto_override) : null,
      descripcion_override: nuevaExc.accion === 'editar' && nuevaExc.descripcion_override ? nuevaExc.descripcion_override : null,
    };
    await dispatch(createException({ itemTipo: excTarget.tipo, itemId: excTarget.id, data }));
    setNuevaExc({ fecha_ocurrencia: hoyISO(), accion: 'omitir', monto_override: '', descripcion_override: '' });
  };

  const handleDeleteExc = async (exceptionId) => {
    if (!excTarget) return;
    await dispatch(deleteException({ exceptionId, itemTipo: excTarget.tipo, itemId: excTarget.id }));
  };

  const currentExceptions = excTarget ? (exceptionsByItem[`${excTarget.tipo}-${excTarget.id}`] || []) : [];

  const renderTable = (items, tipo, color) => (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell>{isSpanish ? 'Nombre' : 'Name'}</TableCell>
            <TableCell>{isSpanish ? 'Frecuencia' : 'Frequency'}</TableCell>
            <TableCell>{isSpanish ? 'Categoría' : 'Category'}</TableCell>
            <TableCell>{isSpanish ? 'Cuenta' : 'Account'}</TableCell>
            <TableCell align="right">{isSpanish ? 'Soles' : 'Soles'}</TableCell>
            <TableCell align="right">{isSpanish ? 'Dólares' : 'Dollars'}</TableCell>
            <TableCell>{isSpanish ? 'Inicio' : 'Start'}</TableCell>
            <TableCell>{isSpanish ? 'Fin' : 'End'}</TableCell>
            <TableCell>{isSpanish ? 'Estado' : 'Status'}</TableCell>
            <TableCell align="center">{isSpanish ? 'Acciones' : 'Actions'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={10}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {isSpanish
                    ? `Sin ${tipo === 'ingreso' ? 'ingresos' : 'egresos'} recurrentes todavía.`
                    : `No recurring ${tipo === 'ingreso' ? 'income' : 'expenses'} yet.`}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {items.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{item.nombre}</TableCell>
              <TableCell>{frecuenciaLabel(item.frecuencia)}</TableCell>
              <TableCell>{item.categoria ? categoriaLabel(item.categoria, isSpanish) : '—'}</TableCell>
              {/* Sin cuenta la serie no aparece al filtrar el calendario
                  por cuenta: se marca en ámbar para que se note. */}
              <TableCell>
                {nombreCuenta(item.account_id) || (
                  <Typography variant="caption" color="warning.main">
                    {isSpanish ? 'Sin asociar' : 'Unlinked'}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {esSoles(item) ? (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
                    S/{formatAmount(item.monto)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {esDolares(item) ? (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
                    ${formatAmount(item.monto)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">—</Typography>
                )}
              </TableCell>
              <TableCell>{item.fecha_inicio}</TableCell>
              <TableCell>{item.fecha_fin || (isSpanish ? 'Indefinido' : 'Indefinite')}</TableCell>
              <TableCell>
                <Chip
                  label={item.activo ? (isSpanish ? 'Activo' : 'Active') : (isSpanish ? 'Pausado' : 'Paused')}
                  size="small"
                  color={item.activo ? 'success' : 'default'}
                  variant="outlined"
                  onClick={() => handleTogglePausa(item, tipo)}
                  clickable
                />
              </TableCell>
              <TableCell align="center">
                <ButtonGroup size="small" variant="text">
                  <Tooltip title={isSpanish ? 'Omitir/editar una ocurrencia' : 'Skip/edit an occurrence'}>
                    <IconButton size="small" onClick={() => handleOpenExc(tipo, item.id, item.nombre)}>
                      <EventBusyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isSpanish ? 'Editar serie' : 'Edit series'}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(item, tipo)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isSpanish ? 'Eliminar serie' : 'Delete series'}>
                    <IconButton size="small" color="error" onClick={() => handleDeleteOpen(tipo, item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Ingresos y Egresos Recurrentes' : 'Recurring Income and Expenses'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish
          ? 'Define series que se repiten (sueldo, alquiler, suscripciones...). Puedes pausarlas, ponerles fecha de fin, u omitir/editar una sola ocurrencia sin afectar el resto de la serie.'
          : "Define series that repeat (salary, rent, subscriptions...). You can pause them, set an end date, or skip/edit a single occurrence without affecting the rest of the series."}
      </Typography>

      {loading && (
        <Box sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">{isSpanish ? 'Cargando...' : 'Loading...'}</Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">
            {isSpanish ? 'Ocurrió un problema al cargar los datos recurrentes.' : 'There was a problem loading the recurring data.'}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
        <Typography variant="h6">{isSpanish ? 'Ingresos recurrentes' : 'Recurring income'}</Typography>
        <Button variant="contained" size="small" color="success" onClick={() => handleOpenNew('ingreso')}>
          {isSpanish ? 'Agregar Ingreso' : 'Add Income'}
        </Button>
      </Box>
      {renderTable(incomes, 'ingreso', 'success')}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 4 }}>
        <Typography variant="h6">{isSpanish ? 'Egresos recurrentes' : 'Recurring expenses'}</Typography>
        <Button variant="contained" size="small" color="error" onClick={() => handleOpenNew('gasto')}>
          {isSpanish ? 'Agregar Egreso' : 'Add Expense'}
        </Button>
      </Box>
      {renderTable(expenses, 'gasto', 'error')}

      {/* Diálogo nuevo */}
      <Dialog open={openNew} onClose={handleCloseNew} fullWidth maxWidth="sm">
        <DialogTitle>
          {isSpanish
            ? `Agregar ${nuevoTipo === 'ingreso' ? 'Ingreso' : 'Egreso'} Recurrente`
            : `Add Recurring ${nuevoTipo === 'ingreso' ? 'Income' : 'Expense'}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nombre"
            label={isSpanish ? 'Nombre' : 'Name'}
            fullWidth
            required
            value={nuevoItem.nombre}
            onChange={handleChange}
            sx={{ mb: 2, mt: 1 }}
            helperText={
              nuevoTipo === 'ingreso'
                ? (isSpanish ? 'Por ejemplo: Sueldo, Renta de alquiler...' : 'For example: Salary, Rental income...')
                : (isSpanish ? 'Por ejemplo: Alquiler, Netflix, Internet...' : 'For example: Rent, Netflix, Internet...')
            }
          />
          <TextField
            margin="dense"
            name="monto"
            label={isSpanish ? 'Monto' : 'Amount'}
            type="number"
            fullWidth
            required
            value={nuevoItem.monto}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="moneda-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
            <Select labelId="moneda-label" name="moneda" value={nuevoItem.moneda} label={isSpanish ? 'Moneda' : 'Currency'} onChange={handleChange}>
              <MenuItem value="PEN">{isSpanish ? 'Soles (S/)' : 'Soles (S/)'}</MenuItem>
              <MenuItem value="USD">{isSpanish ? 'Dólares ($)' : 'Dollars ($)'}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="frecuencia-label">{isSpanish ? 'Frecuencia' : 'Frequency'}</InputLabel>
            <Select labelId="frecuencia-label" name="frecuencia" value={nuevoItem.frecuencia} label={isSpanish ? 'Frecuencia' : 'Frequency'} onChange={handleChange}>
              {frecuencias.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="categoria-label">{isSpanish ? 'Categoría' : 'Category'}</InputLabel>
            <Select labelId="categoria-label" name="categoria" value={nuevoItem.categoria} label={isSpanish ? 'Categoría' : 'Category'} onChange={handleChange}>
              {categorias.map((c) => <MenuItem key={c} value={c}>{categoriaLabel(c, isSpanish)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="cuenta-recurrente-label">{isSpanish ? '¿De qué cuenta o tarjeta sale?' : 'Which account or card?'}</InputLabel>
            <Select
              labelId="cuenta-recurrente-label"
              name="account_id"
              value={nuevoItem.account_id}
              label={isSpanish ? '¿De qué cuenta o tarjeta sale?' : 'Which account or card?'}
              onChange={handleChange}
            >
              <MenuItem value="">{isSpanish ? 'Sin asociar' : 'Not linked'}</MenuItem>
              {cuentas.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}{c.moneda ? ` (${c.moneda})` : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
            {isSpanish
              ? 'Si la dejas sin asociar, esta serie no aparece cuando filtras el calendario por una cuenta.'
              : 'If you leave it unlinked, this series will not show when you filter the calendar by account.'}
          </Typography>
          <TextField
            margin="dense"
            name="fecha_inicio"
            label={isSpanish ? 'Fecha de inicio' : 'Start date'}
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={nuevoItem.fecha_inicio}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="fecha_fin"
            label={isSpanish ? 'Fecha de fin (opcional)' : 'End date (optional)'}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={nuevoItem.fecha_fin}
            onChange={handleChange}
            helperText={isSpanish ? 'Déjalo vacío si la serie no tiene fecha de término.' : "Leave it empty if the series doesn't have an end date."}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNew}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!nuevoItem.nombre?.trim() || !nuevoItem.monto}>
            {isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo editar */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Editar Serie' : 'Edit Series'}</DialogTitle>
        <DialogContent>
          {itemEditando && (
            <>
              <TextField
                autoFocus
                margin="dense"
                name="nombre"
                label={isSpanish ? 'Nombre' : 'Name'}
                fullWidth
                required
                value={itemEditando.nombre}
                onChange={handleEditChange}
                sx={{ mb: 2, mt: 1 }}
              />
              <TextField
                margin="dense"
                name="monto"
                label={isSpanish ? 'Monto' : 'Amount'}
                type="number"
                fullWidth
                value={itemEditando.monto}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="moneda-edit-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
                <Select labelId="moneda-edit-label" name="moneda" value={itemEditando.moneda || 'PEN'} label={isSpanish ? 'Moneda' : 'Currency'} onChange={handleEditChange}>
                  <MenuItem value="PEN">{isSpanish ? 'Soles (S/)' : 'Soles (S/)'}</MenuItem>
                  <MenuItem value="USD">{isSpanish ? 'Dólares ($)' : 'Dollars ($)'}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="frecuencia-edit-label">{isSpanish ? 'Frecuencia' : 'Frequency'}</InputLabel>
                <Select labelId="frecuencia-edit-label" name="frecuencia" value={itemEditando.frecuencia} label={isSpanish ? 'Frecuencia' : 'Frequency'} onChange={handleEditChange}>
                  {frecuencias.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="categoria-edit-label">{isSpanish ? 'Categoría' : 'Category'}</InputLabel>
                <Select labelId="categoria-edit-label" name="categoria" value={itemEditando.categoria || 'Otro'} label={isSpanish ? 'Categoría' : 'Category'} onChange={handleEditChange}>
                  {categorias.map((c) => <MenuItem key={c} value={c}>{categoriaLabel(c, isSpanish)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cuenta-recurrente-edit-label">{isSpanish ? '¿De qué cuenta o tarjeta sale?' : 'Which account or card?'}</InputLabel>
                <Select
                  labelId="cuenta-recurrente-edit-label"
                  name="account_id"
                  value={itemEditando.account_id || ''}
                  label={isSpanish ? '¿De qué cuenta o tarjeta sale?' : 'Which account or card?'}
                  onChange={handleEditChange}
                >
                  <MenuItem value="">{isSpanish ? 'Sin asociar' : 'Not linked'}</MenuItem>
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nombre}{c.moneda ? ` (${c.moneda})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                name="fecha_inicio"
                label={isSpanish ? 'Fecha de inicio' : 'Start date'}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={itemEditando.fecha_inicio}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="fecha_fin"
                label={isSpanish ? 'Fecha de fin (opcional)' : 'End date (optional)'}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={itemEditando.fecha_fin}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!itemEditando.activo}
                    onChange={(e) => setItemEditando((prev) => ({ ...prev, activo: e.target.checked }))}
                  />
                }
                label={itemEditando.activo ? (isSpanish ? 'Activo' : 'Active') : (isSpanish ? 'Pausado' : 'Paused')}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleUpdateSubmit} variant="contained">{isSpanish ? 'Guardar Cambios' : 'Save Changes'}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación */}
      <Dialog open={openDelete} onClose={handleDeleteClose}>
        <DialogTitle>{isSpanish ? '¿Eliminar serie recurrente?' : 'Delete recurring series?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {isSpanish
              ? 'Se eliminará toda la serie y sus excepciones. Esta acción no se puede deshacer.'
              : 'The entire series and its exceptions will be deleted. This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleDelete} color="error" autoFocus>{isSpanish ? 'Eliminar' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de excepciones */}
      <Dialog open={openExc} onClose={handleCloseExc} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? `Ocurrencias de "${excTarget?.nombre}"` : `Occurrences of "${excTarget?.nombre}"`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Omite una fecha puntual (por ejemplo, un mes que no aplica) o edita el monto/descripción de una sola ocurrencia sin tocar el resto de la serie.'
              : "Skip a specific date (for example, a month that doesn't apply) or edit the amount/description of a single occurrence without touching the rest of the series."}
          </Typography>

          <TextField
            margin="dense"
            name="fecha_ocurrencia"
            label={isSpanish ? 'Fecha de la ocurrencia' : 'Occurrence date'}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={nuevaExc.fecha_ocurrencia}
            onChange={handleExcChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="accion-exc-label">{isSpanish ? 'Acción' : 'Action'}</InputLabel>
            <Select labelId="accion-exc-label" name="accion" value={nuevaExc.accion} label={isSpanish ? 'Acción' : 'Action'} onChange={handleExcChange}>
              <MenuItem value="omitir">{isSpanish ? 'Omitir esta ocurrencia' : 'Skip this occurrence'}</MenuItem>
              <MenuItem value="editar">{isSpanish ? 'Editar esta ocurrencia' : 'Edit this occurrence'}</MenuItem>
            </Select>
          </FormControl>

          {nuevaExc.accion === 'editar' && (
            <>
              <TextField
                margin="dense"
                name="monto_override"
                label={isSpanish ? 'Monto para esta ocurrencia' : 'Amount for this occurrence'}
                type="number"
                fullWidth
                value={nuevaExc.monto_override}
                onChange={handleExcChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="descripcion_override"
                label={isSpanish ? 'Descripción para esta ocurrencia' : 'Description for this occurrence'}
                fullWidth
                value={nuevaExc.descripcion_override}
                onChange={handleExcChange}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <Button onClick={handleCreateExc} variant="outlined" size="small" disabled={!nuevaExc.fecha_ocurrencia}>
            {isSpanish ? 'Guardar excepción' : 'Save exception'}
          </Button>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{isSpanish ? 'Excepciones existentes' : 'Existing exceptions'}</Typography>
          {currentExceptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{isSpanish ? 'No hay excepciones registradas.' : 'No exceptions recorded.'}</Typography>
          ) : (
            <List dense>
              {currentExceptions.map((exc) => (
                <ListItem
                  key={exc.id}
                  secondaryAction={
                    <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteExc(exc.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${exc.fecha_ocurrencia} — ${exc.accion === 'omitir' ? (isSpanish ? 'Omitida' : 'Skipped') : (isSpanish ? 'Editada' : 'Edited')}`}
                    secondary={
                      exc.accion === 'editar'
                        ? `${exc.descripcion_override || ''} ${exc.monto_override != null ? `(${exc.monto_override})` : ''}`.trim()
                        : null
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExc}>{isSpanish ? 'Cerrar' : 'Close'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecurringPage;
